import React from 'react';
import { AlertTriangle, Trash2, Info, CheckCircle, X, type LucideIcon } from 'lucide-react';

type Variant = 'danger' | 'warning' | 'info' | 'success';

interface Props {
  open: boolean;
  variant?: Variant;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  error?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const VARIANT_CONFIG: Record<Variant, {
  iconBg: string;
  iconColor: string;
  btnGradient: string;
  btnShadow: string;
  Icon: LucideIcon;
}> = {
  danger: {
    iconBg: 'rgba(239,68,68,0.1)',
    iconColor: '#EF4444',
    btnGradient: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
    btnShadow: '0 4px 14px rgba(239,68,68,0.35)',
    Icon: Trash2,
  },
  warning: {
    iconBg: 'rgba(245,158,11,0.1)',
    iconColor: '#F59E0B',
    btnGradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
    btnShadow: '0 4px 14px rgba(245,158,11,0.35)',
    Icon: AlertTriangle,
  },
  info: {
    iconBg: 'rgba(26,108,246,0.1)',
    iconColor: '#1A6CF6',
    btnGradient: 'linear-gradient(135deg, #1A6CF6 0%, #3B82F6 100%)',
    btnShadow: '0 4px 14px rgba(26,108,246,0.35)',
    Icon: Info,
  },
  success: {
    iconBg: 'rgba(16,185,129,0.1)',
    iconColor: '#10B981',
    btnGradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    btnShadow: '0 4px 14px rgba(16,185,129,0.35)',
    Icon: CheckCircle,
  },
};

const ConfirmDialog: React.FC<Props> = ({
  open, variant = 'danger', title, message,
  confirmLabel = 'Confirm', cancelLabel = 'Cancel',
  isLoading = false, error, onConfirm, onCancel,
}) => {
  if (!open) return null;

  const cfg = VARIANT_CONFIG[variant];
  const { Icon } = cfg;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 400,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}
    >
      {/* Backdrop */}
      <div
        onClick={onCancel}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(8,14,29,0.75)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      />

      {/* Dialog */}
      <div
        style={{
          position: 'relative', zIndex: 1,
          width: '100%', maxWidth: 400,
          background: 'var(--nx-surface-1)',
          borderRadius: 20,
          border: '1px solid var(--nx-border)',
          boxShadow: '0 32px 96px rgba(0,0,0,0.28), 0 8px 24px rgba(0,0,0,0.12)',
          animation: 'nx-scale-in 200ms cubic-bezier(0.34,1.56,0.64,1) both',
          overflow: 'hidden',
        }}
      >
        {/* Top accent line */}
        <div style={{
          height: 3,
          background: cfg.btnGradient,
        }} />

        <div style={{ padding: '24px 24px 20px' }}>
          {/* Header row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flex: 1 }}>
              {/* Icon badge */}
              <div style={{
                width: 44, height: 44, borderRadius: 12, flexShrink: 0,
                background: cfg.iconBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={20} style={{ color: cfg.iconColor }} />
              </div>

              {/* Text */}
              <div style={{ flex: 1, paddingTop: 2 }}>
                <p style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: 15, fontWeight: 700,
                  color: 'var(--nx-text-primary)',
                  marginBottom: 6,
                  letterSpacing: '-0.02em',
                }}>
                  {title}
                </p>
                <p style={{
                  fontSize: 13,
                  color: 'var(--nx-text-secondary)',
                  lineHeight: 1.55,
                }}>
                  {message}
                </p>
              </div>
            </div>

            {/* Close button */}
            <button
              type="button"
              onClick={onCancel}
              style={{
                width: 28, height: 28, flexShrink: 0, marginLeft: 8,
                border: 'none', background: 'var(--nx-surface-3)',
                borderRadius: 7, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--nx-text-muted)',
              }}
            >
              <X size={13} />
            </button>
          </div>

          {/* Error message */}
          {error && (
            <div style={{
              padding: '10px 14px', marginBottom: 16,
              background: 'rgba(239,68,68,0.07)',
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: 8, fontSize: 12, color: '#EF4444',
            }}>
              {error}
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--nx-border)', margin: '0 -24px 16px', marginLeft: -24, marginRight: -24 }} />

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              style={{
                padding: '9px 20px',
                borderRadius: 9,
                border: '1px solid var(--nx-border)',
                background: 'var(--nx-surface-1)',
                color: 'var(--nx-text-secondary)',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 500,
                fontFamily: 'var(--font-body)',
                opacity: isLoading ? 0.5 : 1,
                transition: 'all var(--t-fast)',
              }}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              style={{
                padding: '9px 22px',
                borderRadius: 9,
                border: 'none',
                background: cfg.btnGradient,
                boxShadow: isLoading ? 'none' : cfg.btnShadow,
                color: '#fff',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                fontSize: 13, fontWeight: 600,
                fontFamily: 'var(--font-body)',
                opacity: isLoading ? 0.75 : 1,
                transition: 'all var(--t-fast)',
                display: 'flex', alignItems: 'center', gap: 7,
              }}
            >
              {isLoading && (
                <span style={{
                  width: 12, height: 12,
                  border: '2px solid rgba(255,255,255,0.35)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'nx-spin 0.7s linear infinite',
                  display: 'inline-block', flexShrink: 0,
                }} />
              )}
              {isLoading ? 'Processing…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
